import { upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';

import { paths } from '@common/types';
import axios from 'axios';
import { stableKey } from '@common/cache/util.ts';

interface IOperatorListProps {
  page: number;
  limit: number;
  sort_by?: string;
  sort_order?: string;
  search: string;
  county?: string;
  partner_id?: string;
  state?: string;
}

const operatorsRequest = async (props: IOperatorListProps) => {
  const response = axiosInstance.get<
    paths[`/record/operators`]['get']['responses']['200']['content']['application/json']
  >(`/record/operators`, {
    params: {
      limit: props.limit,
      page: props.page,
      ...(props.search !== '' && { search: props.search }),
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.state !== '' && { state: upperCase(props.state) }),
      ...(props.county !== '' && { county: props.county }),
      ...(props.partner_id !== '' && { partner_id: props.partner_id }),
    },
  });
  const data = (await response).data;

  return data;
};

export const useOperatorListQuery = (props: IOperatorListProps) => {
  const query = useQuery({
    queryKey: ['operators', stableKey(props)],
    queryFn: () => operatorsRequest(props),
    placeholderData: keepPreviousData,
  });

  return query;
};

interface IOperatorProps {
  operator_id: string;
}

const operatorRequest = async (props: IOperatorProps) => {
  const response = axiosInstance.get<
    paths[`/operator/{record_id}`]['get']['responses']['200']['content']['application/json']
  >(`/operator/${props.operator_id}`);
  const data = (await response).data;

  return data;
};

export const useOperatorQuery = (props: IOperatorProps) => {
  const query = useQuery({
    queryKey: ['operator', props.operator_id],
    queryFn: () => operatorRequest(props),
  });

  return query;
};

interface IOperatorHistoryProps {
  page: number;
  limit: number;
  operator_id: string;
  enabled: boolean;
}

const operatorHistoryRequest = async (props: IOperatorHistoryProps) => {
  const response = axiosInstance.get<
    paths[`/operator/{record_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/operator/${props.operator_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const useOperatorHistoryQuery = (props: IOperatorHistoryProps) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['operator', props.operator_id, 'history', props.page],
    queryFn: () => operatorHistoryRequest(props),
  });

  return query;
};

const operatorShortRequest = async (record_id?: string) => {
  const response = axiosInstance.get<
    paths[`/record/operators/short`]['get']['responses']['200']['content']['application/json']
  >(`/record/operators/short`, {
    params: {
      ...(record_id && { record_id }),
    },
  });
  const data = (await response).data;
  return data;
};

export const useOperatorShortQuery = (record_id?: string) => {
  const orgQuery = useQuery({
    queryKey: ['operatorsShort', record_id],
    queryFn: () => operatorShortRequest(record_id),
  });

  return orgQuery;
};

interface IOperatorLogoProps {
  operator_id: string;
}

const operatorLogoRequest = async (
  props: IOperatorLogoProps,
  signal?: AbortSignal,
): Promise<Blob | null> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `/operator/${props.operator_id}/logo.webp`,
      {
        headers: { 'Content-type': 'image/webp' },
        responseType: 'blob',
        signal,
        skipNotFoundRedirect: true,
      },
    );
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};

export const useOperatorLogoQuery = (props: IOperatorLogoProps) => {
  const query = useQuery({
    queryKey: ['operatorLogo', props.operator_id],
    queryFn: () => operatorLogoRequest(props),
  });

  return query;
};
